import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Users, UserPlus, MapPin, Plus, Link } from "lucide-react";
import { useConfirm } from "@tabipla/components";
import {
  useGroup,
  useGroupMembers,
  useGroupTrips,
  useRemoveMember,
  useUpdateMemberRole,
} from "../../application/hooks/useGroups";
import { useSessions, useSession } from "../../application/hooks/useSessions";
import { useAuth } from "../../application/hooks/useAuth";
import { SessionList } from "../components/expenses/SessionList";
import { SessionDetail } from "../components/expenses/SessionDetail";
import { InviteLinkDialog } from "../components/InviteLinkDialog";
import {
  InviteMemberDialog,
  GroupSettingsDialog,
  TripCard,
  MemberCard,
} from "../components/group-detail";
import { Button, Card, CardContent, CardHeader, CardTitle, SegmentedControl } from "@tabipla/ui";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { LoadingState } from "../components/LoadingState";
import { ErrorState } from "../components/ErrorState";
import { EmptyState } from "../components/EmptyState";
import { AnnouncementPanel } from "../components/AnnouncementPanel";
import { GroupBalanceCard } from "../components/GroupBalanceCard";
import { CreateTripDialog } from "../components/CreateTripDialog";
import type { GroupRole, MemberType } from "@tabipla/shared";

export function GroupDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: group, isLoading, error, refetch } = useGroup(id);
  const { data: members } = useGroupMembers(id);
  const { data: trips } = useGroupTrips(id);

  const removeMutation = useRemoveMember(id ?? "");
  const updateRoleMutation = useUpdateMemberRole(id ?? "");

  // Auth
  const { participant } = useAuth();

  // Sessions
  const { data: sessions } = useSessions(id);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const { data: selectedSession } = useSession(id, selectedSessionId ?? undefined);

  const { confirm, ConfirmDialog } = useConfirm();

  // Dialog states
  const [activeTab, setActiveTab] = useState<"overview" | "trips" | "expenses">("overview");
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showInviteLinkDialog, setShowInviteLinkDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showCreateTripDialog, setShowCreateTripDialog] = useState(false);

  const handleRemoveMember = async (memberId: string, memberType: MemberType) => {
    const confirmed = await confirm({
      title: t("groups.removeMemberTitle", "メンバーを削除"),
      description: t("groups.removeMemberConfirm"),
      confirmText: t("common.delete", "削除"),
      variant: "destructive",
    });

    if (confirmed) {
      try {
        await removeMutation.mutateAsync({ memberId, memberType });
        toast.success(t("groups.removeMemberSuccess", "メンバーを削除しました"));
      } catch {
        toast.error(t("groups.removeMemberError", "削除に失敗しました"));
      }
    }
  };

  const handleUpdateRole = async (
    memberId: string,
    memberType: MemberType,
    role: Exclude<GroupRole, "owner">
  ) => {
    try {
      await updateRoleMutation.mutateAsync({ memberId, memberType, role });
      toast.success(t("groups.updateRoleSuccess", "権限を変更しました"));
    } catch {
      toast.error(t("groups.updateRoleError", "変更に失敗しました"));
    }
  };

  // Count non-completed sessions for badge
  const activeSessionCount = sessions?.filter((s) => s.status !== "completed").length ?? 0;

  if (isLoading) {
    return (
      <DashboardLayout title={t("groups.detail")}>
        <LoadingState message={t("groups.loadingDetail")} />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title={t("groups.detail")}>
        <ErrorState error={error} onRetry={refetch} />
      </DashboardLayout>
    );
  }

  if (!group) {
    return (
      <DashboardLayout title={t("groups.detail")}>
        <ErrorState error={new Error(t("groups.notFound"))} />
      </DashboardLayout>
    );
  }

  const isOwner = group.myRole === "owner";
  const canEdit = isOwner || group.myRole === "editor";

  return (
    <DashboardLayout
      title={group.name}
      fabAction={
        isOwner
          ? {
              icon: UserPlus,
              label: t("groups.invite"),
              onClick: () => setShowInviteDialog(true),
            }
          : undefined
      }
    >
      <div className="animate-fade-in space-y-6">
        {/* Cover Image Banner */}
        {group.coverImageUrl && (
          <div className="relative -mx-4 -mt-4 overflow-hidden sm:-mx-6 lg:-mx-8">
            <img
              src={group.coverImageUrl}
              alt=""
              className="h-32 w-full object-cover sm:h-40 lg:h-48"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
          </div>
        )}

        {/* Background gradient */}
        {group.backgroundColor && !group.coverImageUrl && (
          <div
            className={`-mx-4 -mt-4 h-24 bg-gradient-to-br sm:-mx-6 lg:-mx-8 ${group.backgroundColor}`}
          />
        )}

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {/* Group Icon */}
            {group.iconImageUrl ? (
              <img
                src={group.iconImageUrl}
                alt=""
                className="h-12 w-12 rounded-xl object-cover shadow-md"
              />
            ) : group.iconEmoji ? (
              <div
                className="flex h-12 w-12 items-center justify-center rounded-xl text-2xl shadow-md"
                style={{ backgroundColor: group.iconColor ?? "#e5e7eb" }}
              >
                {group.iconEmoji}
              </div>
            ) : null}
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{group.name}</h1>
              {group.description && (
                <p className="mt-1 text-muted-foreground">{group.description}</p>
              )}
            </div>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowSettingsDialog(true)}>
                {t("groups.settings")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowInviteLinkDialog(true)}>
                <Link className="mr-2 h-4 w-4" />
                {t("invitations.linkInvite")}
              </Button>
            </div>
          )}
        </div>

        {/* Balance Card - Owner Only */}
        {isOwner && (
          <GroupBalanceCard
            groupId={id ?? ""}
            onNavigateToExpenses={() => setActiveTab("expenses")}
          />
        )}

        {/* Tab Navigation */}
        <SegmentedControl
          value={activeTab}
          onChange={(v) => setActiveTab(v as "overview" | "trips" | "expenses")}
          options={[
            { value: "overview", label: t("groups.overview", "概要") },
            { value: "trips", label: t("groups.trips", "旅行") },
            {
              value: "expenses",
              label: t("groups.expenses", "精算"),
              badge: activeSessionCount > 0 ? activeSessionCount : undefined,
            },
          ]}
        />

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-4">
            {/* Members */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-base">
                  <Users className="mr-2 h-5 w-5" />
                  {t("groups.members")} ({members?.length ?? 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {members?.map((member) => (
                  <MemberCard
                    key={`${member.memberType}:${member.memberId}`}
                    memberId={member.memberId}
                    memberType={member.memberType}
                    memberName={member.memberName}
                    memberEmail={member.memberEmail}
                    role={member.role}
                    isOwner={isOwner}
                    onUpdateRole={handleUpdateRole}
                    onRemove={handleRemoveMember}
                  />
                ))}
              </CardContent>
            </Card>

            {/* Announcements */}
            <AnnouncementPanel groupId={id ?? ""} isOwner={isOwner} />
          </div>
        )}

        {activeTab === "trips" && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="flex items-center text-base">
                <MapPin className="mr-2 h-5 w-5" />
                {t("groups.groupTrips")}
              </CardTitle>
              {canEdit && (
                <Button size="sm" onClick={() => setShowCreateTripDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("groups.addTrip")}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {!trips || trips.length === 0 ? (
                <EmptyState
                  icon={MapPin}
                  title={t("groups.noTrips")}
                  description={t("groups.noTripsDescription", "旅行を追加して計画を始めましょう")}
                  variant="default"
                />
              ) : (
                <div className="space-y-2">
                  {trips.map((trip) => (
                    <TripCard key={trip.id} trip={trip} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "expenses" && (
          <>
            {selectedSessionId && selectedSession ? (
              <SessionDetail
                groupId={id ?? ""}
                session={selectedSession}
                members={members ?? []}
                currentUserId={participant?.id ?? ""}
                currentUserType={participant?.type ?? "user"}
                onBack={() => setSelectedSessionId(null)}
                canEdit={canEdit}
              />
            ) : (
              <SessionList
                groupId={id ?? ""}
                sessions={sessions ?? []}
                onSessionSelect={setSelectedSessionId}
                canEdit={canEdit}
              />
            )}
          </>
        )}
      </div>

      {/* Dialogs */}
      <InviteMemberDialog
        groupId={id ?? ""}
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
      />

      {group && (
        <GroupSettingsDialog
          groupId={id ?? ""}
          group={group}
          open={showSettingsDialog}
          onOpenChange={setShowSettingsDialog}
        />
      )}

      <InviteLinkDialog
        open={showInviteLinkDialog}
        onOpenChange={setShowInviteLinkDialog}
        groupId={id ?? ""}
        groupName={group?.name}
      />

      <CreateTripDialog
        open={showCreateTripDialog}
        onOpenChange={setShowCreateTripDialog}
        groupId={id}
      />

      <ConfirmDialog />
    </DashboardLayout>
  );
}
